import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "@/hooks/use-toast";
import { CalendarIcon, Phone, Mail, MapPin, Instagram, MessageCircle, Clock, Send } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    eventType: "",
    date: undefined as Date | undefined,
    message: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      toast({
        title: "Demande envoyée avec succès ! 💖",
        description: "Gem vous contactera sous 48h pour confirmer les détails de votre commande.",
      });
      setIsSubmitting(false);
      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        eventType: "",
        date: undefined,
        message: ""
      });
    }, 1000);
  };

  const eventTypes = [
    "Anniversaire",
    "Mariage", 
    "Baptême",
    "Baby shower",
    "Fête d'entreprise",
    "Saint-Valentin",
    "Fête des mères",
    "Graduation",
    "Autre événement"
  ];

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-pink-50/30 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            🎂 Réservez votre gâteau sur mesure
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Chez Sweets by Gem, chaque gâteau est préparé sur commande, selon vos goûts et vos envies. 
            Remplissez le formulaire ci-dessous ou contactez-nous directement — nous vous répondrons sous 48 heures.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="shadow-elegant border-pink-100">
              <CardHeader>
                <CardTitle className="text-2xl text-gray-900 flex items-center">
                  <Send className="w-6 h-6 mr-3 text-primary" />
                  Formulaire de réservation
                </CardTitle>
                <CardDescription>
                  Remplissez ce formulaire pour recevoir un devis personnalisé sous 48h
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name and Email */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nom & Prénom *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="Votre nom complet"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Adresse e-mail *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="votre@email.com"
                        required
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+33 6 12 34 56 78"
                      required
                    />
                  </div>

                  {/* Event Type and Date */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Type d'événement *</Label>
                      <Select value={formData.eventType} onValueChange={(value) => setFormData({...formData, eventType: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionnez votre événement" />
                        </SelectTrigger>
                        <SelectContent>
                          {eventTypes.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Date souhaitée *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.date ? format(formData.date, "PPP", { locale: fr }) : "Choisir une date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={formData.date}
                            onSelect={(date) => setFormData({...formData, date})}
                            initialFocus
                            locale={fr}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <Label htmlFor="message">Message personnalisé *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      placeholder="Décrivez votre gâteau idéal : type (Layer Cake, Cupcakes...), saveur, nombre de parts, budget estimé, couleurs, thème, décoration souhaitée, allergies..."
                      rows={6}
                      required
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full gradient-primary text-white shadow-elegant text-lg py-6"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        🍰 Envoyer ma demande
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Information */}
          <div className="space-y-6">
            {/* Direct Contact */}
            <Card className="border-primary/20 bg-gradient-to-br from-pink-50 to-rose-50">
              <CardHeader>
                <CardTitle className="text-xl text-gray-900 flex items-center">
                  <Phone className="w-5 h-5 mr-2 text-primary" />
                  Contact direct
                </CardTitle>
                <CardDescription>
                  Préférez échanger directement ? Contactez Gemima
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <Button
                    variant="outline"
                    className="w-full justify-start border-primary text-primary hover:bg-primary hover:text-white"
                    onClick={() => window.open("https://wa.me/33753797698", "_blank")}
                  >
                    <MessageCircle className="w-4 h-4 mr-3" />
                    WhatsApp : +33 7 53 79 76 98
                  </Button>
                  
                  <Button
                    variant="outline"
                    className="w-full justify-start border-gray-300 text-gray-700 hover:bg-gray-50"
                    onClick={() => window.open("mailto:gemimankdu9@icloud.com", "_blank")}
                  >
                    <Mail className="w-4 h-4 mr-3" />
                    gemimankdu9@icloud.com
                  </Button>
                  
                  <Button
                    variant="outline"
                    className="w-full justify-start border-accent text-accent hover:bg-accent hover:text-white"
                    onClick={() => window.open("https://www.instagram.com/sweetsby_gem", "_blank")}
                  >
                    <Instagram className="w-4 h-4 mr-3" />
                    @sweetsby_gem
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Location & Hours */}
            <Card className="border-accent/20 bg-gradient-to-br from-yellow-50 to-orange-50">
              <CardHeader>
                <CardTitle className="text-xl text-gray-900 flex items-center">
                  <MapPin className="w-5 h-5 mr-2 text-accent" />
                  Atelier & Horaires
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2 text-sm text-gray-700">
                  <div className="flex items-start">
                    <MapPin className="w-4 h-4 mr-2 text-accent mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">3 rue Magenta</div>
                      <div>69100 Villeurbanne</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <Clock className="w-4 h-4 mr-2 text-accent mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Lundi - Samedi</div>
                      <div>9h00 - 18h00</div>
                      <div className="text-xs text-gray-500 mt-1">
                        Atelier sur rendez-vous uniquement
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Trust Indicators */}
            <Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
              <CardContent className="pt-6">
                <div className="text-center space-y-3">
                  <h4 className="font-semibold text-gray-900">✨ Nos engagements</h4>
                  <div className="space-y-2 text-sm text-gray-700">
                    <div>❤️ Tous nos gâteaux sont faits maison</div>
                    <div>🌸 Ingrédients frais et de qualité</div>
                    <div>📦 Livraison disponible sur Lyon</div>
                    <div>⏰ Réponse garantie sous 48h</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* How to Order */}
        <div className="mt-16">
          <Card className="shadow-elegant border-pink-100">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl text-gray-900">
                Commander en 3 étapes simples
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-primary">1</span>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Choisissez votre gâteau
                  </h4>
                  <p className="text-sm text-gray-600">
                    Sélectionnez le type de gâteau ou envoyez-nous votre idée créative
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-primary">2</span>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Personnalisez
                  </h4>
                  <p className="text-sm text-gray-600">
                    Définissez la taille, la saveur, les couleurs et la décoration
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-primary">3</span>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Confirmez
                  </h4>
                  <p className="text-sm text-gray-600">
                    Réservez la date et recevez votre confirmation avec le devis
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;